import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
  CheckThenCreateEc2ConfigDocument,
  CheckThenCreateEc2ConfigInput,
  DeleteEc2ConfigDocument,
  DeleteEc2ConfigInput,
  ListEc2ConfigsQuery,
  UpdateEc2ConfigInput,
  UpdateEc2ConfigDocument,
  useListEc2ConfigsQuery,
  useListLabDetailsQuery,
  CreateEc2ConfigInput,
  Lab,
  LabStatus,
} from '../lib/api';
import {
  CreateEc2ConfigDocument,
  Ec2Config,
} from '../lib/api';
import {
  onCreateEc2Config,
  onDeleteEc2Config,
  onUpdateEc2Config,
} from '../graphql/subscriptions';

import { API } from '../lib/fetcher';

import Observable from 'zen-observable';

// import Datetime from 'react-datetime';
// import "react-datetime/css/react-datetime.css";
// import { Moment } from "moment";

import { AppContext } from '../App';

import './configs.css';

const initialState = {
  lab: Lab.Kaliwin,
  userId: 'noUserId',
  minutes: 40,
  skipCfn: false,
};

export interface RouteParams {
  id?: string;
}

// Countdown component to show when a lab should be ready
const LabReadyCountdown = ({ creationTimeDuration, creationTimeDurationOffset, creationTime, terminateTimestamp, labStatus, onRefetch }: {
  creationTimeDuration: number,
  creationTimeDurationOffset: number,
  creationTime: number,
  terminateTimestamp: number,
  labStatus?: LabStatus,
  onRefetch: () => void
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [creationTimeDurationLeft, setCreationTimeDurationLeft] = useState<number>(0);
  // const [isLabReady, setIsLabReady] = useState<boolean>(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000);
      const remainingCreationTimeDuration = creationTime - now + (creationTimeDuration - creationTimeDurationOffset);
      setCreationTimeDurationLeft(remainingCreationTimeDuration);

      const remainingUntilTermination = terminateTimestamp - now;
      setTimeLeft(remainingUntilTermination);

    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [creationTimeDuration, creationTimeDurationOffset, creationTimeDurationLeft, creationTime, terminateTimestamp, labStatus]);

  // Add auto-refetch effect when timeLeft reaches 0
  useEffect(() => {
    let refetchInterval: NodeJS.Timeout | null = null;

    if (creationTimeDurationLeft <= 0 && labStatus !== LabStatus.Created) {
      // Initial refetch
      onRefetch();

      refetchInterval = setInterval(() => {
        console.log('Running scheduled refetch');
        onRefetch();
      }, 5000);
    }

    // Cleanup function
    return () => {
      if (refetchInterval) {
        clearInterval(refetchInterval);
      }
    };
  }, [creationTimeDurationLeft, labStatus, onRefetch]);

  const timeLeftMinutes = Math.floor(timeLeft / 60);
  const timeLeftSeconds = timeLeft % 60;

  const creationTimeDurationMinutes = Math.floor(creationTimeDurationLeft / 60);
  const creationTimeDurationSeconds = creationTimeDurationLeft % 60;

  if (labStatus === 'CREATED') {
    if (timeLeft <= 0) {
      return <span className="text-red-500">Lab is about to be terminated</span>;
    }
    return (
      <span className="lab-terminating">
        {timeLeftMinutes}m {timeLeftSeconds}s
      </span>
    );
  }

  if (labStatus === 'CREATE_IN_PROGRESS') {
    return (
      <span className="lab-pending">
        Ready in: {creationTimeDurationLeft > 0 ? `${creationTimeDurationMinutes}m ${creationTimeDurationSeconds}s` : 'soon'}
      </span>
    );
  }

  return (
    <span className="lab-pending">Lab is still being created...</span>
  );
};

export function Configs() {
  const [config, setConfig] = useState(initialState);
  const { userId, minutes } = config;
  const { id } = useParams();
  const [extensionMinutes, setExtensionMinutes] = useState<{ [key: string]: number }>({});
  const [creationTimeDurationOffset, setCreationTimeDurationOffset] = useState<number>(0);


  useEffect(() => {
    subscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, refetch } = useListEc2ConfigsQuery(null, {
    refetchOnWindowFocus: false,
    queryKey: ['listEc2Configs'],
  });

  const { data: labDetailsData } = useListLabDetailsQuery(null, {
    refetchOnWindowFocus: false,
    queryKey: ['listLabDetails'],
  });

  const subscribe = async () => {
    const updateSub = (await API.getInstance().query(
      onUpdateEc2Config
    )) as Observable<UpdateEc2ConfigInput>;
    updateSub.subscribe({
      next: async (message) => {
        console.log('Finished updateSub fired');
        console.log(message);
        await refetch();
        // setMessagess(['doint']);
      },
      error: (error) => console.warn(error),
    });

    const createSub = (await API.getInstance().query(
      onCreateEc2Config
    )) as Observable<CreateEc2ConfigInput>;
    createSub.subscribe({
      next: async (message) => {
        console.log('Finished createSub fired');
        console.log(message);
        await refetch();
        // setMessagess(['doint']);
      },
      error: (error) => console.warn(error),
    });

    const deleteSub = (await API.getInstance().query(
      onDeleteEc2Config
    )) as Observable<DeleteEc2ConfigInput>;
    deleteSub.subscribe({
      next: async (message) => {
        console.log('Finished deleteSub fired');
        console.log(message);
        await refetch();
        // setMessagess(['doint']);
      },
      error: (error) => console.warn(error),
    });
  };

  // useCreatePostMutation isn't working correctly right now
  const createEc2Config = useMutation({
    mutationFn: async (input: CreateEc2ConfigInput) => {
      const result = await API.getInstance().query(CreateEc2ConfigDocument, {
        input,
      });
      return result.data?.createEc2Config as Ec2Config;
    },
  });

  const createEc2ConfigWithApiKey = useMutation({
    mutationFn: async (input: CreateEc2ConfigInput) => {
      const result = await API.getInstance().queryWithApiKey(
        CreateEc2ConfigDocument,
        { input }
      );
      return result.data?.createEc2Config as Ec2Config;
    }
  });

  const checkThenCreateEc2Config = useMutation({
    mutationFn: async (input: CheckThenCreateEc2ConfigInput) => {
      const result = await API.getInstance().queryWithApiKey(
        CheckThenCreateEc2ConfigDocument,
        { input }
      );
      return result.data?.checkThenCreateEc2Config as Ec2Config;
    }
  });

  const deleteEc2Config = useMutation({
    mutationFn: async (input: DeleteEc2ConfigInput) => {
      const result = await API.getInstance().query(DeleteEc2ConfigDocument, {
        input,
      });
      console.log(result);
      return result.data?.deleteEc2Config as Ec2Config;
    }
  });

  const updateEc2Config = useMutation({
    mutationFn: async (input: UpdateEc2ConfigInput) => {
      const result = await API.getInstance().query(UpdateEc2ConfigDocument, {
        input,
      });
      return result.data?.updateEc2Config as Ec2Config;
    }
  });

  const createNewEc2Config = async (
    data: ListEc2ConfigsQuery,
    useApiKey?: boolean
  ) => {
    if (!userId || minutes < 1) return;
    if (data?.listEc2Configs.items.length > 50) return;

    console.log(config);

    const input: CreateEc2ConfigInput = {
      lab: config.lab,
      userId: config.userId,
      finished: false,
      skipCfn: config.skipCfn,
      // terminateTimestamp: new Date(new Date().getTime() + minutes * 60000).getTime(),
      terminateTimestamp:
        Math.floor(new Date().getTime() / 1000) + minutes * 60, // frankfurt doesn't seem to be in summer time
      // startDate: startDate2.toISOString(),
      //userId: userData.userId,
    };

    const createResult = useApiKey
      ? await createEc2ConfigWithApiKey.mutateAsync(input, {
        onSuccess: (data) => {
          console.log(data);
        },
      })
      : await createEc2Config.mutateAsync(input, {
        onSuccess: (data) => {
          console.log(data);
        },
      });
    if (createResult) {
      data = (await refetch()).data;
    }
  };

  const checkThenCreateNewEc2Config = async (data: ListEc2ConfigsQuery) => {
    if (!userId || minutes < 1) return;
    if (data?.listEc2Configs.items.length > 50) return;

    console.log(config);

    const input: CreateEc2ConfigInput = {
      lab: config.lab,
      userId: config.userId,
      finished: false,
      skipCfn: config.skipCfn,
      // terminateTimestamp: new Date(new Date().getTime() + minutes * 60000).getTime(),
      terminateTimestamp:
        Math.floor(new Date().getTime() / 1000) + minutes * 60, // frankfurt doesn't seem to be in summer time
      // startDate: startDate2.toISOString(),
      //userId: userData.userId,
    };

    const createResult = await checkThenCreateEc2Config.mutateAsync(input, {
      onSuccess: (data) => {
        console.log(data);
      },
    });
    if (createResult) {
      data = (await refetch()).data;
    }
  };

  const extendLabDuration = async (configId: string, minutes: number) => {
    if (!configId || minutes === 0) return;

    const configToExtend = data?.listEc2Configs.items.find(c => c.id === configId);
    if (!configToExtend) return;

    const newTerminateTimestamp = configToExtend.terminateTimestamp + (minutes * 60);

    // Ensure the new timestamp is not in the past
    const currentTimestamp = Math.floor(Date.now() / 1000);
    if (newTerminateTimestamp <= currentTimestamp) {
      console.error("Cannot reduce lab time below current time");
      return;
    }

    const input: UpdateEc2ConfigInput = {
      id: configId,
      terminateTimestamp: newTerminateTimestamp,
    };

    const updateResult = await updateEc2Config.mutateAsync(input, {
      onSuccess: (data) => {
        console.log('Lab time updated successfully:', data);
      },
    });

    if (updateResult) {
      await refetch();
      // Reset extension minutes for this config
      setExtensionMinutes(prev => ({ ...prev, [configId]: 0 }));
    }
  };

  const onChange = (e: any) => {
    setConfig(() => ({ ...config, [e.target.name]: e.target.value }));
  };

  if (isLoading) return <div className="loader"></div>;

  return (
    <AppContext.Consumer>
      {({ ec2List }) => (
        <div className="configs-container">
          <div>
            <h2 className="configs-header">Lab Config</h2>
            {/* <p className="api-key">API Key: {API.getInstance().getApiKey()}</p> */}

            {data?.listEc2Configs?.items && data.listEc2Configs.items.length > 0 ? (
              <div className="configs-list">
                {data?.listEc2Configs?.items
                  ?.filter((c) => !id || (id && c.id === id))
                  .sort((c1, c2) => c1.lab.localeCompare(c2.lab))
                  .map((config) => {
                    // Find matching lab detail
                    const labDetail = labDetailsData?.listLabDetails?.items?.find(detail =>
                      detail.lab === config.lab
                    );

                    return (
                      <div key={config.id} className="config-card">
                        <div className="config-header">
                          <p className="config-id">ID: {config.id}</p>
                          {config.finished ? null : <div className="loader"></div>}
                        </div>

                        <h3 className="config-user">User: {config.userId}</h3>

                        <div className="config-detail">
                          <span className="config-label">Lab:</span>
                          <span className="config-value">{config.lab}</span>
                        </div>

                        <div className="config-detail">
                          <span className="config-label">Skip CFN:</span>
                          <span className="config-value">{config.skipCfn.toString()}</span>
                        </div>

                        <div className="config-detail">
                          <span className="config-label">Lab Status:</span>
                          <span className="config-value">{config.labStatus || 'Loading...'}</span>
                        </div>

                        <div className="config-detail">
                          <span className="config-label">Created:</span>
                          <span className="config-value">{new Date(config.createdAt).toLocaleString()}</span>
                        </div>

                        <div className="config-detail">
                          <span className="config-label">Remaining Time:</span>
                          {labDetail?.lastCreationTimeInSeconds ? (
                            <LabReadyCountdown
                              creationTimeDuration={labDetail.lastCreationTimeInSeconds}
                              creationTimeDurationOffset={creationTimeDurationOffset}
                              creationTime={Math.floor(new Date(config.createdAt).getTime() / 1000)}
                              terminateTimestamp={config.terminateTimestamp}
                              labStatus={config.labStatus}
                              onRefetch={refetch}
                            />
                          ) : (
                            <LabReadyCountdown
                              creationTimeDuration={420}
                              creationTimeDurationOffset={creationTimeDurationOffset}
                              creationTime={Math.floor(new Date(config.createdAt).getTime() / 1000)}
                              terminateTimestamp={config.terminateTimestamp}
                              labStatus={config.labStatus}
                              onRefetch={refetch}
                            />
                          )}
                        </div>

                        <div className="config-detail">
                          <span className="config-label">Terminating At:</span>
                          <span className="config-value">{new Date(config.terminateTimestamp * 1000).toLocaleString()}</span>
                        </div>

                        {!config.finished && (
                          <div className="border-t border-gray-200 mt-4 pt-4 flex flex-col gap-3">
                            <div className="flex flex-col">
                              <label className="text-sm font-medium text-gray-600 mb-2">Adjust lab time (minutes):</label>
                              <input
                                type="number"
                                value={extensionMinutes[config.id] || ""}
                                onChange={(e) => setExtensionMinutes({
                                  ...extensionMinutes,
                                  [config.id]: parseInt(e.target.value) || 0
                                })}
                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                placeholder="Enter +/- minutes"
                              />
                              <span className="text-xs text-gray-500 mt-1">
                                Use positive values to extend or negative values to reduce time
                              </span>
                            </div>
                            <button
                              className={`py-2 px-4 rounded-md text-white text-sm font-medium transition-colors ${!extensionMinutes[config.id] || extensionMinutes[config.id] === 0
                                ? 'bg-gray-300 cursor-not-allowed'
                                : extensionMinutes[config.id] > 0
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-amber-500 hover:bg-amber-600'
                                }`}
                              disabled={!extensionMinutes[config.id] || extensionMinutes[config.id] === 0}
                              onClick={() => extendLabDuration(config.id, extensionMinutes[config.id])}
                            >
                              {extensionMinutes[config.id] > 0
                                ? `Extend Lab by ${extensionMinutes[config.id]} min`
                                : extensionMinutes[config.id] < 0
                                  ? `Reduce Lab by ${Math.abs(extensionMinutes[config.id])} min`
                                  : 'Update Lab Time'}
                            </button>
                          </div>
                        )}

                        {config.finished && (
                          <div className="config-detail">
                            <span className="config-label">Associated VMs:</span>
                            <span className="config-value">
                              {ec2List?.listEc2
                                ?.filter(
                                  (e) =>
                                    e.userId === config.userId &&
                                    e.lab === config.lab
                                )
                                .map((e) => (
                                  <a key={e.id} href={'#/vms/' + e.id} className="vm-link">
                                    {e.id}
                                  </a>
                                ))}
                            </span>
                          </div>
                        )}

                        <button
                          className="mt-4 py-2 px-4 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                          onClick={async () => {
                            const deleteResult = await deleteEc2Config.mutateAsync(
                              { id: config.id }
                            );
                            if (deleteResult) {
                              refetch();
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="no-configs">
                <h4>No lab configs found</h4>
              </div>
            )}
          </div>

          <div className="form-container">
            <h3 className="form-title">Create Lab Config</h3>

            <div className="form-group">
              <label className="form-label">User ID:</label>
              <input
                onChange={onChange}
                name="userId"
                placeholder="martin"
                className="input-field"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Lab Type:</label>
              <ul className="lab-options">
                {Object.values(Lab).map((labType) => (
                  <li key={labType} className="lab-option">
                    <input
                      type="radio"
                      id={labType.toLowerCase()}
                      value={labType}
                      name="lab"
                      defaultChecked={labType === Lab.Kaliwin}
                      onChange={onChange}
                    />
                    {labType}
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-group">
              <label className="form-label">Minutes: {config.minutes}</label>
              <input
                onChange={onChange}
                name="minutes"
                type="number"
                min="1"
                placeholder={config.minutes.toString()}
                className="input-field"
              />
              <span className="text-xs text-gray-500 mt-1">
                Offset for lab creation time in seconds
              </span>
              <input
                type="number"
                value={creationTimeDurationOffset}
                onChange={(e) => setCreationTimeDurationOffset(parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="180"
              />
            </div>

            <div className="checkbox-container">
              <input
                type="checkbox"
                name="skipCfn"
                onChange={(e) => (config.skipCfn = e.target.checked)}
                defaultChecked={config.skipCfn}
              />
              <label>Skip CFN</label>
            </div>

            <div className="actions">
              <button
                className="create-button"
                onClick={() => createNewEc2Config(data)}
              >
                Create with IAM
              </button>

              <button
                className="api-button"
                onClick={() => createNewEc2Config(data, true)}
              >
                Create with API Key
              </button>

              <button
                className="check-create-button"
                onClick={() => checkThenCreateNewEc2Config(data)}
              >
                Check then Create
              </button>
            </div>
          </div>
        </div>
      )}
    </AppContext.Consumer>
  );
}
